import React, { useEffect } from "react";
import { GetServerSidePropsContext, NextPage } from "next";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getDispensaryReviews from "api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { CountryCode } from "custom-types/CountryCode";
import { Dispensary } from "custom-types/Dispensary";
import { DispensaryReview } from "custom-types/Reviews";
import { reviewsMetadata } from "lib/dispensaryMetadata";
import {
  COMPLIANCE_RULESET_LOAD,
  ComplianceRuleset,
} from "redux/reducers/complianceRules";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import { getFirstQueryParam } from "utils/nextRouterUtils";
import { getCountryCodeHeader } from "utils/requestContext";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";

import DispensaryBreadcrumbs from "components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import NoReviews from "components/Dispensary/Reviews/components/NoReviews";
import ReviewContainer, {
  ReviewType,
} from "components/Dispensary/Reviews/components/ReviewContainer";
import ReviewsHeader from "components/Dispensary/ReviewsHeader";
import MetaTags from "components/MetaTags";
import PageNavigationV2 from "components/PageNavigationV2";
import Sort from "components/Sort";

const DISPENSARY_REVIEWS_PER_PAGE = 24;

/* eslint-disable sort-keys-fix/sort-keys-fix -- exception: order important to display */
const sortOptions: { [key: string]: string } = {
  "Most Helpful": "upvotes_count:desc",
  "Least Helpful": "upvotes_count:asc",
  Newest: "created:desc",
  Oldest: "created:asc",
};
/* eslint-enable sort-keys-fix/sort-keys-fix -- exception: order important to display */

const ReviewsPage: NextPage<{
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
  reviews: DispensaryReview[];
  totalReviews: number;
}> = ({ complianceRuleset, dispensary, reviews, totalReviews }) => {
  const router = useRouter();
  const dispatch = useDispatch();

  const { coverPhotoUrl, featureTier, logoUrl, name } = dispensary;

  useTrackDispensaryPageViewSplitEvent({
    page: "reviews",
    tier: featureTier,
  });

  useEffect(() => {
    if (complianceRuleset) {
      dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }
  }, []);

  const page = Math.max(Number(getFirstQueryParam(router.query.page) ?? 1), 1);

  const hasReviews = totalReviews > 0;

  const { description, image, title } = reviewsMetadata(
    coverPhotoUrl,
    logoUrl,
    name,
    page,
  );

  return (
    <>
      <MetaTags
        canonicalParams={["page"]}
        description={description}
        image={image}
        robots={!hasReviews ? "noindex,noarchive" : undefined}
        title={title}
      />
      <DispensaryHeader dispensary={dispensary} reviewCount={totalReviews} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.reviews}
        dispensary={dispensary}
      />
      {hasReviews ? (
        <>
          <ReviewsHeader
            dispensary={dispensary}
            className="container mt-xl lg:mt-section"
          />
          <section className="container mb-section mt-xl">
            {reviews.length && (
              <Sort
                className="self-end mb-sm"
                sortOptions={sortOptions}
                shallowRouting={false}
              />
            )}
            {reviews.map((review) => (
              <ReviewContainer
                key={review.id}
                retailType={dispensary.retailType}
                review={review}
                type={ReviewType.DISPENSARY}
                itemReviewed={dispensary.name}
                dispensaryAddress={{
                  address1: dispensary.address1,
                  city: dispensary.city,
                  country: dispensary.country,
                  state: dispensary.state,
                  zip: dispensary.zip,
                }}
              />
            ))}
            <PageNavigationV2
              currentPage={page}
              showRelativeLinkTags
              totalPages={Math.ceil(totalReviews / DISPENSARY_REVIEWS_PER_PAGE)}
            />
            <DispensaryBreadcrumbs dispensary={dispensary} />
          </section>
        </>
      ) : (
        <NoReviews dispensary={dispensary} />
      )}
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { query, req, resolvedUrl } = context;
  const { slug } = query;

  const dispensary = await getDispensary(slug as string);

  const domainCountryCode = getCountryCodeHeader(req.headers) as CountryCode;

  const redirect = generateDispensaryRedirect(
    domainCountryCode,
    resolvedUrl,
    query,
    dispensary,
  );

  if (redirect) {
    return { redirect };
  }

  const page = Math.max(Number(getFirstQueryParam(query.page, "1")), 1);
  const sort = getFirstQueryParam(query.sort, sortOptions["Most Helpful"]);

  const [{ reviews, totalReviews }, complianceRuleset] = await Promise.all([
    getDispensaryReviews(
      slug as string,
      (page - 1) * DISPENSARY_REVIEWS_PER_PAGE,
      DISPENSARY_REVIEWS_PER_PAGE,
      encodeURI(sort),
    ),
    getRegionComplianceRules(dispensary?.state, dispensary?.country),
  ]);

  const pageDoesNotExist =
    page > 1 && totalReviews / DISPENSARY_REVIEWS_PER_PAGE < page - 1;

  if (pageDoesNotExist) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      complianceRuleset,
      dispensary,
      reviews,
      totalReviews,
    },
  };
};

export default ReviewsPage;
